/** ManagerConference (P5.1 §C) — the manager's meeting surface. Adapted from the prebuilt
 * `<VideoConference>` composition (which exposes no way to filter tiles or add controls), trimmed to a
 * grid (a manager call is ~3 tiles, no screen share) and changed in two ways:
 *   1. the LiveKit job-worker tile is filtered out (`isWorkerIdentity`) — the LiveAvatar tile stays;
 *   2. a "Hide avatar" control sends a `closeAvatar` `RoomControlCommand` on `ROOM_CONTROL_TOPIC`; the
 *      agent tears the avatar down while staying connected (and keeps transcribing).
 * Tile names are native — the visitor/manager carry a token `name` and the avatar is named at the
 * source (`avatarParticipantName`), so `<ParticipantTile>` renders friendly names with no override.
 * Active-speaker highlighting comes from the grid (it updates on `ActiveSpeakersChanged`). */
import { ROOM_CONTROL_TOPIC, type RoomControlCommand } from "@evas/shared";
import {
  ConnectionStateToast,
  ControlBar,
  DisconnectButton,
  formatChatMessageLinks,
  GridLayout,
  LayoutContextProvider,
  LeaveIcon,
  ParticipantTile,
  RoomAudioRenderer,
  useCreateLayoutContext,
  useRoomContext,
  useTracks,
  type WidgetState,
} from "@livekit/components-react";
import { RoomEvent, Track } from "livekit-client";
import { useCallback, useState } from "react";
import { ManagerChat } from "./ManagerChat.js";
import { PrivateChat } from "./PrivateChat.js";
import { isWorkerIdentity } from "./participants.js";

export function ManagerConference() {
  const room = useRoomContext();
  const layoutContext = useCreateLayoutContext();
  const [widgetState, setWidgetState] = useState<WidgetState>({
    showChat: false,
    unreadMessages: 0,
    showSettings: false,
  });
  // P5.4 private manager↔agent chat. Independent of the public chat panel's `showChat` (they render
  // side by side when both are open) — deliberately its own control so the manager can never confuse
  // "private to the assistant" with "everyone in the call sees this".
  const [showPrivate, setShowPrivate] = useState(false);
  const revealPrivate = useCallback(() => setShowPrivate(true), []);

  // Camera tiles (+ placeholders for camera-less participants), minus the job-worker. The avatar's
  // real video keeps its tile; the visitor/manager get camera or placeholder tiles.
  const tracks = useTracks([{ source: Track.Source.Camera, withPlaceholder: true }], {
    updateOnlyOn: [RoomEvent.ActiveSpeakersChanged],
    onlySubscribed: false,
  }).filter((t) => !isWorkerIdentity(t.participant.identity));

  const hideAvatar = useCallback(() => {
    const cmd: RoomControlCommand = { v: 1, type: "closeAvatar" };
    void room.localParticipant
      .sendText(JSON.stringify(cmd), { topic: ROOM_CONTROL_TOPIC })
      .catch((err) => console.error("hide-avatar command failed", err));
  }, [room]);

  return (
    <div className="lk-video-conference">
      <LayoutContextProvider value={layoutContext} onWidgetChange={setWidgetState}>
        <div className="lk-video-conference-inner">
          <GridLayout tracks={tracks}>
            <ParticipantTile />
          </GridLayout>
          {/* Native controls minus Leave (mic/camera/screen-share/chat), then our "Ask privately" +
              "Hide avatar" controls, then our own Leave — so ours sit between Chat and Leave. */}
          <div className="manager-controls">
            <ControlBar controls={{ chat: true, leave: false, settings: false }} />
            <button
              type="button"
              className="manager-private-toggle"
              aria-pressed={showPrivate}
              onClick={() => setShowPrivate((v) => !v)}
              title="Ask the assistant a question privately — the visitor neither sees nor hears it"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M8 1a2 2 0 0 1 2 2v4a2 2 0 1 1-4 0V3a2 2 0 0 1 2-2" />
                <path d="M5 6.5a.5.5 0 0 1 .5.5v.5a2.5 2.5 0 0 0 5 0V7a.5.5 0 0 1 1 0v.5a3.5 3.5 0 0 1-3 3.464V13h1.5a.5.5 0 0 1 0 1h-4a.5.5 0 0 1 0-1H7.5v-2.036A3.5 3.5 0 0 1 4.5 7.5V7a.5.5 0 0 1 .5-.5" />
              </svg>
              Ask privately
            </button>
            <button
              type="button"
              className="manager-hide-avatar"
              onClick={hideAvatar}
              title="Hide the AI avatar for everyone (the assistant stays in the call)"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M13.359 11.238C15.06 9.72 16 8 16 8s-3-5.5-8-5.5a7 7 0 0 0-2.79.588l.77.771A6 6 0 0 1 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13 13 0 0 1 14.828 8q-.086.13-.195.288c-.335.48-.83 1.12-1.465 1.755q-.247.248-.517.486z" />
                <path d="M11.297 9.176a3.5 3.5 0 0 0-4.474-4.474l.823.823a2.5 2.5 0 0 1 2.829 2.829zm-2.943 1.299.822.822a3.5 3.5 0 0 1-4.474-4.474l.823.823a2.5 2.5 0 0 0 2.829 2.829z" />
                <path d="M3.35 5.47c-.18.16-.353.322-.518.487A13 13 0 0 0 1.172 8l.195.288c.335.48.83 1.12 1.465 1.755C4.121 11.332 5.881 12.5 8 12.5q.716.001 1.39-.16l.74.74A7 7 0 0 1 8 13.5C3 13.5 0 8 0 8s.939-1.721 2.641-3.238l.708.709zM1.354.646a.5.5 0 1 0-.708.708l14 14a.5.5 0 0 0 .708-.708z" />
              </svg>
              Hide avatar
            </button>
            <DisconnectButton>
              <LeaveIcon />
              Leave
            </DisconnectButton>
          </div>
        </div>
        {/* Our chat (P5.5) instead of the prebuilt <Chat>: adds a file picker and renders received
            images/files (the prefab has no picker, and its entry can't render received attachments —
            see ManagerChat.tsx). */}
        <ManagerChat
          messageFormatter={formatChatMessageLinks}
          style={{ display: widgetState.showChat ? "grid" : "none" }}
        />
        {/* Always mounted (so a private answer arriving while the panel is closed isn't missed);
            visibility is toggled inside. `onBriefing` REVEALS it: the EVAS-117 join briefing arrives
            unasked, seconds after this room mounts, and a briefing nobody sees is the same as no
            briefing at all. Only that unsolicited line opens the panel — an ordinary answer cannot,
            since the manager had to open it to ask in the first place. */}
        <PrivateChat visible={showPrivate} onBriefing={revealPrivate} />
      </LayoutContextProvider>
      <RoomAudioRenderer />
      <ConnectionStateToast />
    </div>
  );
}
